import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

export interface SearchSelectOption {
   value: string;
   label: string;
   /** Optional secondary text shown muted next to the label (e.g. subnet). */
   sublabel?: string;
}

interface Props {
   value: string;
   onChange: (value: string) => void;
   options: SearchSelectOption[];
   placeholder?: string;
   searchPlaceholder?: string;
   emptyText?: string;
   disabled?: boolean;
   error?: boolean;
   /** Hide the search box (for short lists). Defaults to on. */
   searchable?: boolean;
}

/**
 * Searchable single-select. Unlike a native <select>, the option list is a
 * scrollable, filterable panel with a capped height — so it stays usable when
 * there are hundreds of items (e.g. a vendo with many VLAN zones).
 */
const SearchSelect = ({
   value,
   onChange,
   options,
   placeholder = 'Select…',
   searchPlaceholder = 'Search…',
   emptyText = 'No matches',
   disabled,
   error,
   searchable = true,
}: Props) => {
   const [open, setOpen] = useState(false);
   const [q, setQ] = useState('');
   const wrapRef = useRef<HTMLDivElement>(null);

   const selected = options.find((o) => o.value === value);

   const filtered = useMemo(() => {
      const needle = q.trim().toLowerCase();
      if (!needle) return options;
      return options.filter(
         (o) =>
            o.label.toLowerCase().includes(needle) ||
            (o.sublabel ?? '').toLowerCase().includes(needle)
      );
   }, [options, q]);

   // Close on click outside.
   useEffect(() => {
      if (!open) return;
      const onDocClick = (e: MouseEvent) => {
         if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
            setOpen(false);
            setQ('');
         }
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
   }, [open]);

   const pick = (v: string) => {
      onChange(v);
      setOpen(false);
      setQ('');
   };

   return (
      <Wrap ref={wrapRef}>
         <Control
            type="button"
            disabled={disabled}
            $error={!!error}
            $open={open}
            onClick={() => !disabled && setOpen((o) => !o)}
         >
            <ControlText $placeholder={!selected}>
               {selected ? selected.label : placeholder}
               {selected?.sublabel && <Sub> · {selected.sublabel}</Sub>}
            </ControlText>
            <Chevron $open={open}>▾</Chevron>
         </Control>

         {open && (
            <Panel role="listbox">
               {searchable && (
                  <SearchBox
                     autoFocus
                     placeholder={searchPlaceholder}
                     value={q}
                     onChange={(e) => setQ(e.target.value)}
                  />
               )}
               <OptionList>
                  {filtered.length === 0 ? (
                     <Empty>{emptyText}</Empty>
                  ) : (
                     filtered.map((o) => (
                        <OptionRow
                           key={o.value}
                           type="button"
                           $active={o.value === value}
                           onClick={() => pick(o.value)}
                        >
                           <span>{o.label}</span>
                           {o.sublabel && <Sub>{o.sublabel}</Sub>}
                        </OptionRow>
                     ))
                  )}
               </OptionList>
            </Panel>
         )}
      </Wrap>
   );
};

export default SearchSelect;

const Wrap = styled.div`
   position: relative;
   width: 100%;
`;

const Control = styled.button<{ $error: boolean; $open: boolean }>`
   width: 100%;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   padding: 9px 12px;
   background: ${colors.WHITE};
   border: 1px solid
      ${(p) => (p.$error ? colors.DANGER : p.$open ? colors.PRIMARY : `${colors.PRIMARY}30`)};
   border-radius: 8px;
   font-size: 13px;
   cursor: pointer;
   text-align: left;
   &:disabled {
      background: ${colors.GRAY1}10;
      color: ${colors.GRAY1};
      cursor: not-allowed;
   }
`;

const ControlText = styled.span<{ $placeholder: boolean }>`
   color: ${(p) => (p.$placeholder ? colors.GRAY1 : colors.MAIN_DARK)};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const Sub = styled.span`
   color: ${colors.GRAY1};
   font-size: 12px;
`;

const Chevron = styled.span<{ $open: boolean }>`
   color: ${colors.GRAY1};
   font-size: 12px;
   transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
   transition: transform 0.15s;
`;

const Panel = styled.div`
   position: absolute;
   top: calc(100% + 4px);
   left: 0;
   right: 0;
   background: ${colors.WHITE};
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 8px;
   box-shadow: ${colors.BOXSHADOW};
   z-index: 20;
   padding: 6px;
   max-width: 100%;
`;

const SearchBox = styled.input`
   width: 100%;
   padding: 8px 10px;
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 6px;
   font-size: 13px;
   margin-bottom: 6px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const OptionList = styled.div`
   max-height: 240px;
   overflow-y: auto;
`;

const OptionRow = styled.button<{ $active: boolean }>`
   width: 100%;
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 10px;
   padding: 8px 10px;
   border: none;
   border-radius: 6px;
   background: ${(p) => (p.$active ? `${colors.PRIMARY}12` : 'transparent')};
   color: ${colors.MAIN_DARK};
   font-size: 13px;
   text-align: left;
   cursor: pointer;
   &:hover {
      background: ${colors.PRIMARY}10;
   }
`;

const Empty = styled.div`
   padding: 12px 10px;
   color: ${colors.GRAY1};
   font-size: 13px;
   text-align: center;
`;
